<template>
    <b-modal
      v-model="data.showModal"
      button-size="sm"
      modal-class="invoice-modal"
      title-class="w-100"
      footer-class="w-100"
      no-fade
      @show="onShow"
      size="xl"
      :no-close-on-backdrop="true"
      scrollable
    >
      <!-- Modal header -->
      <div slot="modal-title">
        <h1 class="flex flex-column gap-2 w-50">
          <div class="text-2xl font-bold">Invoice</div>
          <div v-if="invoice" class="text-xl">{{ invoice.invoice_code }}</div>
        </h1>
      </div>

      <!-- Modal content -->
      <div v-if="invoice" class="flex flex-column gap-4">
        <table class="table-invoice">
          <tbody>
            <tr>
              <td class="row-fit">Type</td>
              <td>
                <button-select
                  v-model="invoice.invoice_type"
                  :options="options.invoice_type"
                  size="sm"
                  class="w-50"
                />
              </td>
            </tr>

            <tr>
              <td class="row-fit">Supplier</td>
              <td>
                <v-select
                  v-model="invoice.partner_id"
                  :options="suppliers"
                  size="sm"
                  label="text"
                  :reduce="option => option.value"
                />
              </td>
            </tr>

            <tr>
              <td class="row-fit">Date</td>
              <td>
                <date-picker
                  class="date-picker w-50"
                  v-model="invoice.issue_date"
                  format="DD.MM.YYYY"
                  valueType="YYYY-MM-DD"
                  :clearable="false"
                  :lang="datepickerLocale"
                />
              </td>
            </tr>

            <tr>
              <td class="row-fit">Invoice</td>
              <td>
                <b-form-input
                  v-model="invoice.invoice_code"
                  autocomplete="off"
                  :class="{ 'border-1 border-red-500': !invoiceValidation.invoice_code }"
                  size="sm"
                  trim
                ></b-form-input>
              </td>
            </tr>

            <tr>
              <td class="row-fit">Amount</td>
              <td>
                <b-input-group>
                  <b-form-input
                    v-model="invoice.price_brutto"
                    autocomplete="off"
                    :class="{ 'border-1 border-red-500': !invoiceValidation.price_brutto }"
                    size="sm"
                  ></b-form-input>

                  <template #append>
                    <b-form-select
                      v-model="invoice.currency_id"
                      :options="options.currencies"
                      size="sm"
                    ></b-form-select>
                  </template>
                </b-input-group>

              </td>
            </tr>

            <tr>
              <td>Notes</td>
              <td>
                <b-form-textarea
                  v-model="invoice.invoice_note"
                  rows="6"
                  max-rows="6"
                  trim
                ></b-form-textarea>
              </td>
            </tr>

            <tr>
              <td>Files</td>
              <td>
                <table class="table table-google table-invoices">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th class="row-fit">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="invoice.files.length === 0">
                      <td colspan="2">No files</td>
                    </tr>
                    <tr v-for="file in invoice.files" :key="file.id">
                      <td>
                        <b-link
                          :href="file.link"
                          target="_blank"
                          class="custom-link"
                        >
                          {{ file.filename }}
                        </b-link>
                      </td>
                      <td class="row-fit">
                        <div>
                          <b-btn
                            variant="danger"
                            size="sm"
                            @click="deleteFile(file)"
                          >
                            Delete
                          </b-btn>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <b-form-file
                  ref="upload"
                  v-model="upload"
                  required
                  :state="Boolean(upload)"
                  placeholder="Choose a file.."
                  label-size="sm"
                  :disabled="isLoading"
                  @change.native="uploadFile"
                  no-drop
                  class="upload"
                ></b-form-file>
              </td>
            </tr>

            <tr>
              <td>Purchases</td>
              <td>
                <table class="table table-google table-highlight table-purchases">
                  <thead>
                    <tr>
                      <th class="text-nowrap">Group</th>
                      <th class="text-nowrap">Option</th>
                      <th class="text-nowrap">Price</th>
                      <th class="text-nowrap text-right">Rate</th>
                      <th class="text-nowrap text-right">CZK</th>
                      <th class="text-nowrap">Unit Type</th>
                      <th class="text-nowrap">Unit</th>
                      <th class="text-nowrap">Quantity</th>
                      <th><!-- Actions --></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="purchases === null || purchases.length === 0">
                      <td colspan="9">No purchases</td>
                    </tr>
                    <tr v-for="purchase in purchases" :key="purchase.guid">
                      <td class="row-fit">
                        <b-form-select
                          v-model="purchase.group_key"
                          :options="purchaseGroupOptions"
                          size="sm"
                          @change="onPurchaseGroup(purchase)"
                        ></b-form-select>
                      </td>
                      <td class="row-fit">
                        <v-select
                          v-model="purchase.option_id"
                          :options="groupOptions[purchase.group_key] || []"
                          label="text"
                          :reduce="option => option.value"
                          placeholder="Select option"
                          appendToBody
                          :calculate-position="calculatePosition"
                        />
                      </td>
                      <td class="row-fit">
                        <b-input-group size="sm" class="flex-nowrap">
                          <b-form-input
                            v-model="purchase.purchase_price"
                            class="text-right"
                            placeholder="Price"
                            trim
                            autocomplete="off"
                          ></b-form-input>
                          <template #append>
                            <b-input-group-text>
                              {{ invoiceCurrencyCode }}
                            </b-input-group-text>
                          </template>
                        </b-input-group>
                      </td>
                      <td class="row-fit text-nowrap text-right">
                        <span v-if="purchase.currency_rate !== null">
                          {{ roundNumber(purchase.currency_rate, 2, 6) }}
                        </span>
                      </td>
                      <td class="row-fit text-nowrap text-right">
                        {{ getPurchaseCzk(purchase) }}
                      </td>
                      <td class="row-fit">
                        <b-form-select
                          v-model="purchase.unit_type"
                          :options="options.unit_type"
                          size="sm"
                          @change="onUnitType(purchase)"
                        ></b-form-select>
                      </td>
                      <td class="row-fit">
                        <b-form-select
                          v-model="purchase.unit_code"
                          :options="getUnitOptions(purchase.unit_type)"
                          size="sm"
                        ></b-form-select>
                      </td>
                      <td class="row-fit">
                        <b-form-input
                          v-model="purchase.unit_value"
                          class="text-right"
                          placeholder="Quantity"
                          size="sm"
                          trim
                          autocomplete="off"
                        ></b-form-input>
                      </td>
                      <td class="row-fit">
                        <b-btn
                          size="sm"
                          variant="danger"
                          :disabled="isLoading"
                          @click="deletePurchase(purchase)"
                          title="Delete purchase"
                        >
                          &times;
                        </b-btn>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot v-if="purchases !== null && purchases.length > 0">
                    <tr>
                      <td colspan="2"></td>
                      <td class="text-nowrap font-weight-bold text-right">
                        {{ roundNumber(purchaseTotal, 2, 2) }} {{ invoiceCurrencyCode }}
                      </td>
                      <td colspan="6"></td>
                    </tr>
                  </tfoot>
                </table>
                <b-btn
                  size="sm"
                  variant="primary"
                  :disabled="isLoading || purchases === null"
                  @click="createPurchase"
                >
                  Add purchase
                </b-btn>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="isLoading">
        Loading..
      </div>

      <!-- Modal footer -->
      <div slot="modal-footer" class="w-100 flex justify-between gap-4 items-center">
        <div>
          <b-btn
            variant="primary"
            size="sm"
            @click="saveInvoice()"
            :disabled="!canSave"
          >
            Save
          </b-btn>
        </div>

        <div>
          <b-btn
            variant="danger"
            size="sm"
            @click="deleteInvoice()"
          >
            Delete
          </b-btn>
        </div>
      </div>
    </b-modal>
  </template>

<script>
import {
  calculateDropdownPosition,
  datepickerLocale,
  generateGuid,
  getUnitTypeOptions,
  numberToString,
  productUnits,
  roundNumber,
  stringToNumber,
} from '@/helpers';

const ButtonSelect = () => import('@/components/global/ButtonSelect.vue');

export default {
  name: 'InvoiceModal',
  components: {
    ButtonSelect,
  },
  props: {
    data: Object,
    suppliers: Array,
  },
  computed: {
    invoiceData() {
      const invoiceData = { ...this.invoice };
      invoiceData.price_brutto = stringToNumber(invoiceData.price_brutto);
      return invoiceData;
    },
    invoiceValidation() {
      return {
        invoice_code: this.invoiceData.invoice_code.length > 0,
        price_brutto: !isNaN(this.invoiceData.price_brutto) && this.invoiceData.price_brutto !== 0,
      };
    },
    datepickerLocale() {
      return datepickerLocale;
    },
    isLoading() {
      return this.loadingCount > 0;
    },
    purchaseGroupOptions() {
      return this.purchaseGroups.map((group) => ({
        text: group.group_name,
        value: group.group_key,
      }));
    },
    invoiceCurrencyCode() {
      if (!this.invoice) {
        return '';
      }
      const currency = this.options.currencies.find(
        (record) => record.value === this.invoice.currency_id,
      );
      return currency ? currency.text : '';
    },
    purchaseTotal() {
      if (this.purchases === null) {
        return 0;
      }
      return this.purchases.reduce((total, purchase) => {
        const price = stringToNumber(purchase.purchase_price);
        return isNaN(price) ? total : total + price;
      }, 0);
    },
    sendPurchases() {
      if (this.purchases === null) {
        return null;
      }
      const sendPurchases = this.purchases.map((purchase) => ({
        id: purchase.id,
        option_id: purchase.option_id,
        currency_id: purchase.currency_id,
        currency_rate: purchase.currency_rate,
        purchase_date: purchase.purchase_date,
        purchase_price: stringToNumber(purchase.purchase_price),
        purchase_price_czk: stringToNumber(purchase.purchase_price) * purchase.currency_rate,
        unit_type: purchase.unit_type,
        unit_code: purchase.unit_code,
        unit_value: stringToNumber(purchase.unit_value),
      }));
      return sendPurchases;
    },
    canSave() {
      if (!this.invoice) return false;
      if (this.isLoading) return false;
      if (this.purchases !== null) {
        const purchasesValid = this.purchases.every(
          (purchase) => this.isPurchaseValid(purchase),
        );
        if (!purchasesValid) return false;
      }
      return Object.values(this.invoiceValidation).every((val) => val === true);
    },
  },
  data() {
    return {
      loadingCount: 0,
      invoice: null,
      upload: null,
      purchases: null,
      purchaseGroups: [],
      groupOptions: {},
      options: {
        invoice_type: [
          { text: 'Invoice', value: 'invoice' },
          { text: 'Proforma', value: 'proforma' },
        ],
        unit_type: getUnitTypeOptions(),
        currencies: this.$store.state.app.currencies.map((currency) => ({
          text: currency.currency_code,
          value: currency.id,
        })),
      },
    };
  },
  watch: {
    'invoice.issue_date': function watchIssueDate() {
      this.syncPurchaseDates();
    },
    'invoice.currency_id': function watchCurrencyId() {
      this.syncPurchaseCurrencies();
    },
  },
  methods: {
    syncPurchaseDates() {
      if (!this.invoice || this.purchases === null) {
        return;
      }
      this.purchases.forEach((purchase) => {
        if (purchase.purchase_date !== this.invoice.issue_date) {
          purchase.purchase_date = this.invoice.issue_date;
          this.fetchRate(purchase);
        }
      });
    },
    syncPurchaseCurrencies() {
      if (!this.invoice || this.purchases === null) {
        return;
      }
      this.purchases.forEach((purchase) => {
        if (purchase.currency_id !== this.invoice.currency_id) {
          purchase.currency_id = this.invoice.currency_id;
          this.fetchRate(purchase);
        }
      });
    },
    onShow() {
      this.fetchInvoice();
      this.fetchPurchaseGroups().then(() => {
        this.fetchPurchases();
      });
    },
    onClose() {
      this.data.showModal = false;
      this.$emit('close');
    },
    roundNumber(value, minimumFractionDigits, maximumFractionDigits) {
      return roundNumber(value, minimumFractionDigits, maximumFractionDigits);
    },
    calculatePosition(dropdownList, component) {
      return calculateDropdownPosition(dropdownList, component);
    },
    isPurchaseValid(purchase) {
      if (purchase.group_key === null) return false;
      if (purchase.option_id === null) return false;
      if (purchase.purchase_date === null || purchase.purchase_date.length === 0) return false;
      if (purchase.currency_rate === null || isNaN(purchase.currency_rate)) return false;
      if (purchase.purchase_price.length === 0) return false;
      if (isNaN(stringToNumber(purchase.purchase_price))) return false;
      if (purchase.unit_value.length === 0) return false;
      if (isNaN(stringToNumber(purchase.unit_value))) return false;
      return true;
    },
    getPurchaseCzk(purchase) {
      if (!this.isPurchaseValid(purchase)) {
        return '';
      }
      const purchaseCzk = stringToNumber(purchase.purchase_price) * purchase.currency_rate;
      return roundNumber(purchaseCzk, 2, 2);
    },
    getUnitOptions(unitType) {
      return Object.keys(productUnits[unitType])
        .map((unit) => ({ text: unit, value: unit }));
    },
    onUnitType(purchase) {
      const defaultUnit = Object.entries(productUnits[purchase.unit_type])
        .find((unit) => unit[1] === 1)[0];
      purchase.unit_code = defaultUnit;
    },
    onPurchaseGroup(purchase) {
      purchase.option_id = null;
      this.ensureGroupOptions(purchase.group_key);
    },
    fetchPurchaseGroups() {
      this.loadingCount++;
      return this.$http
        .get('/select/groups')
        .then((res) => {
          this.purchaseGroups = res.body.groups.filter((group) => group.has_purchase);
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch groups: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    ensureGroupOptions(groupKey) {
      if (groupKey === null || this.groupOptions[groupKey] !== undefined) {
        return;
      }
      this.$set(this.groupOptions, groupKey, []);
      const group = this.purchaseGroups.find((record) => record.group_key === groupKey);
      this.loadingCount++;
      this.$http
        .get(`/select/group_options/${groupKey}`)
        .then((res) => {
          const prefix = group ? group.option_prefix : '';
          const suffix = group ? group.option_suffix : '';
          this.$set(this.groupOptions, groupKey, res.body.map((option) => ({
            text: `${prefix}${option.option_value}${suffix}`,
            value: option.id,
          })));
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch options: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchPurchases() {
      this.loadingCount++;
      this.$http
        .get(`/select/invoice_purchases/${this.data.invoice.id}`)
        .then((res) => {
          res.body.forEach((purchase) => {
            purchase.guid = generateGuid();
            purchase.purchase_price = numberToString(purchase.purchase_price);
            purchase.unit_value = numberToString(purchase.unit_value);
            this.ensureGroupOptions(purchase.group_key);
          });
          this.purchases = res.body;
          this.syncPurchaseDates();
          this.syncPurchaseCurrencies();
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch purchases: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchRate(purchase) {
      if (purchase.purchase_date === null || purchase.purchase_date.length === 0) {
        return;
      }
      purchase.currency_rate = null;
      this.loadingCount++;
      this.$http
        .get(`/currency/rate/${purchase.currency_id}/${purchase.purchase_date}`)
        .then((res) => {
          purchase.currency_rate = res.body.currency_rate;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch currency rate: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    createPurchase() {
      const newPurchase = {
        id: null,
        guid: generateGuid(),
        group_key: null,
        option_id: null,
        currency_id: this.invoice.currency_id,
        currency_rate: null,
        purchase_date: this.invoice.issue_date,
        purchase_price: '',
        unit_type: 'amount',
        unit_code: 'amount',
        unit_value: '1',
      };
      this.purchases.push(newPurchase);
      this.fetchRate(newPurchase);
    },
    deletePurchase(deletePurchase) {
      if (deletePurchase.id !== null && !confirm('Do you really wish to delete this purchase?')) {
        return;
      }
      this.purchases = this.purchases.filter((purchase) => purchase.guid !== deletePurchase.guid);
    },
    savePurchases() {
      if (this.purchases === null) {
        return Promise.resolve();
      }
      this.loadingCount++;
      return this.$http
        .put(`/select/invoice_purchases/${this.data.invoice.id}`)
        .send({ data: this.sendPurchases })
        .catch((err) => {
          this.$toast.error(`Failed to update purchases: ${err.response.text}`);
          throw err;
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchInvoice() {
      this.loadingCount++;
      this.$http
        .get(`/invoice/${this.data.invoice.id}`)
        .then((res) => {
          this.invoice = res.body.invoice;
          this.invoice.price_brutto = numberToString(this.invoice.price_brutto);
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch invoice: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    saveInvoice() {
      this.loadingCount++;
      this.$http
        .put(`/invoice/${this.data.invoice.id}`)
        .send({ changes: this.invoiceData })
        .then(() => this.savePurchases())
        .then(() => {
          this.$emit('change', this.data.invoice.id);
          this.onClose();
        })
        .catch((err) => {
          this.$toast.error(`Failed to update invoice: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    deleteInvoice() {
      if (!confirm(`Do you really wish to delete invoice '${this.data.invoice.invoice_code}'?`)) {
        return;
      }
      this.loadingCount++;
      this.$http
        .delete(`/invoice/${this.data.invoice.id}`)
        .then(() => {
          this.$emit('delete', this.data.invoice.id);
          this.onClose();
        })
        .catch((err) => {
          this.$toast.error(`Failed to delete invoice: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    uploadFile() {
      this.loadingCount++;
      const formData = new FormData();
      formData.append('upload', this.upload);
      formData.append('mtime', this.upload.lastModified / 1000);
      this.$http
        .post(`/invoice/${this.data.invoice.id}/files`)
        .send(formData)
        .then((res) => {
          this.invoice.files.push(res.body.invoiceFile);
          this.$emit('change', this.data.invoice.id);
        })
        .catch((err) => {
          this.$toast.error(`Failed to upload file: ${err.response.text}`);
        })
        .finally(() => {
          this.upload = null;
          this.loadingCount--;
        });
    },
    deleteFile(file) {
      if (!confirm(`Do you really wish to delete file '${file.filename}'?`)) {
        return;
      }
      this.loadingCount++;
      this.$http
        .delete(`/invoice/${this.data.invoice.id}/files/${file.id}`)
        .then(() => {
          this.invoice.files = this.invoice.files.filter((f) => f.id !== file.id);
          this.$emit('change', this.data.invoice.id);
        })
        .catch((err) => {
          this.$toast.error(`Failed to delete file: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
  .table-invoice {
    tr {
      border-top: 5px solid transparent;
    }

    tr > td:first-child {
      padding-right: 10px;
    }
  }

  .table-purchases {
    tfoot td {
      // right padding aligns the total with the currency label in the price inputs
      padding: 7px 22px 8px 8px;
    }

    input.form-control,
    select.custom-select {
      field-sizing: content;
      width: auto;
      flex: 0 0 auto;
      min-width: 4.5rem;
      max-width: 16rem;
    }
  }

  .input-group > .input-group-append > .custom-select {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-left: -1px;
  }

  .table-purchases ::v-deep .v-select {
    min-width: 12rem;
    max-width: 24rem;

    .vs__selected-options {
      flex-wrap: nowrap;
    }

    .vs__selected {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .vs__search {
      min-width: 0;
    }
  }
</style>
